import { useMutation, useQueryClient } from '@tanstack/react-query'
import ActivityLog from '../../model/ActivityLog'
import Badge from '../../model/Badge'
import { dataSource } from '../../service/domain/_service.config'
import { logError } from '../../util/errorLogger'
import { useChallengeId } from '../challenge/useChallengeId'
import { useAllBadges } from './useAllBadges'
import { TEAM_BADGES_QUERY_KEY } from './useTeamBadges'

export function useCheckTeamBadges() {
  const queryClient = useQueryClient()
  const { data: allBadges } = useAllBadges()
  const challengeID = useChallengeId()

  return useMutation({
    mutationFn: async ({
      teamID,
      earnedByUserID,
      teamActivityLogs,
    }: {
      teamID: string
      earnedByUserID: string
      teamActivityLogs: ActivityLog[]
    }): Promise<Badge[]> => {
      if (!allBadges) return []

      const [teamBadgesData, activities] = await Promise.all([
        dataSource.getTeamBadges(teamID),
        dataSource.getActivities(challengeID),
      ])
      const activityMap = new Map(activities.map((a) => [a.id, a]))
      const earnedBadgeIDs = new Set(teamBadgesData.map((eb) => eb.badge.id))

      const newlyEarnedBadges: Badge[] = []
      const earnedAt = new Date()

      const calculateValues = (badge: Badge, logs: ActivityLog[]) => {
        const relevantLogs = logs.filter((log) =>
          badge.appliesToActivity(log.activityID, activityMap.get(log.activityID)),
        )
        switch (badge.requirementType) {
          case 'count':
            return { current: 0, cumulative: relevantLogs.length }
          case 'time':
          case 'distance':
            return {
              current: 0,
              cumulative: relevantLogs.reduce((sum, log) => sum + log.amount, 0),
            }
          case 'streak':
            return { current: 0, cumulative: 0 }
          default:
            return { current: 0, cumulative: 0 }
        }
      }

      const teamBadges = allBadges.filter((b) => b.scope === 'team')
      for (const badge of teamBadges) {
        if (earnedBadgeIDs.has(badge.id) || !badge.isActive) continue

        const { cumulative } = calculateValues(badge, teamActivityLogs)
        if (badge.checkRequirementsMet(0, cumulative, undefined, undefined)) {
          await dataSource.saveTeamBadge(teamID, badge.id, earnedAt, earnedByUserID)
          newlyEarnedBadges.push(badge)
        }
      }

      if (newlyEarnedBadges.length > 0) {
        queryClient.invalidateQueries({ queryKey: TEAM_BADGES_QUERY_KEY(teamID) })
      }

      return newlyEarnedBadges
    },
    onError: (error) => {
      void logError(error, { hook: 'useCheckTeamBadges' })
    },
  })
}
